import { trackEconomyAction } from 'analytics';
import { Tier } from 'dragonmouse-shared/interfaces';
import { VITE_FORGE_URL } from '../../../constants';
import * as S from './styles';

// Pricing constants
const PRO_MONTHLY = '12';
const PRO_YEARLY = '10.20';
const MAX_MONTHLY = '42';
const MAX_YEARLY = '35.70';
const UPGRADE_MAX_DIFFERENCE_MONTHLY = '30';
const UPGRADE_MAX_DIFFERENCE_YEARLY = '25.50';

interface TierCardProps {
	tier: Tier.TIER_1 | Tier.TIER_2; // tier to upgrade to
	variant: 'current' | 'upgrade' | 'upgradeToMax'; // upgradeToMax is for people already on tier 1 -- it shows the price difference
	renewalDate?: string;
	isYearly?: boolean;
}

const TierCard = ({ variant, tier, renewalDate, isYearly }: TierCardProps) => {
	const tierName = tier === Tier.TIER_1 ? 'Pro' : 'Max';

	const billingUrl =
		import.meta.env.VITE_APP_ENV === 'production'
			? 'https://billing.stripe.com/p/login/5kQfZb8Dofdy8o4d7s4ZG00'
			: 'https://billing.stripe.com/p/login/test_28E00d90L4Dv73dbgjao800';

	const handleManageBillingClick = () => {
		trackEconomyAction('manage_subscription_clicked');
	};

	const handleUpgradeRedirect = () => {
		trackEconomyAction('upgrade_redirected');
	};

	const renderFeatureList = () => {
		const PRO_FEATURES = [
			'Unlimited projects',
			'Always-on Silver Access',
			'Code Export',
			'Export to Figma',
		];
		const MAX_FEATURES = [
			'Everything in Pro',
			'Always-on Gold Access',
			'Design systems',
			'Priority support',
		];
		const features = tier === Tier.TIER_1 ? PRO_FEATURES : MAX_FEATURES;
		return (
			<S.Features>
				{features.map((feature) => (
					<S.Feature key={feature} tier={tier}>
						<S.Checkmark tier={tier} viewBox="0 0 20 20" fill="currentColor">
							<path
								fillRule="evenodd"
								d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
								clipRule="evenodd"
							/>
						</S.Checkmark>
						{feature}
					</S.Feature>
				))}
			</S.Features>
		);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	if (variant === 'current') {
		return (
			<S.Base isCurrent tier={tier}>
				<S.CurrentPlanBadge>Current Plan</S.CurrentPlanBadge>
				<S.Name tier={tier}>{tierName}</S.Name>
				{renewalDate && <S.RenewalInfo>Renews on {renewalDate}</S.RenewalInfo>}
				{renderFeatureList()}
				<S.LinkWrapper
					href={billingUrl}
					target="_blank"
					rel="noopener noreferrer"
					onClick={handleManageBillingClick}
				>
					<S.CTAButton variant="manage">Manage Billing</S.CTAButton>
				</S.LinkWrapper>
				<S.CancelLink href={billingUrl} target="_blank" rel="noopener noreferrer">
					Cancel subscription
				</S.CancelLink>
			</S.Base>
		);
	}

	if (variant === 'upgradeToMax') {
		const priceDifference = isYearly
			? UPGRADE_MAX_DIFFERENCE_YEARLY
			: UPGRADE_MAX_DIFFERENCE_MONTHLY;

		return (
			<S.Base tier={Tier.TIER_2}>
				<S.UpgradeHeadline>Upgrade to Max</S.UpgradeHeadline>
				<S.UpgradeDescription>More gold and premium support</S.UpgradeDescription>
				<S.Pricing>
					<S.PriceDifference>
						+${priceDifference}
						<S.PricePeriod>/mo</S.PricePeriod>
					</S.PriceDifference>
				</S.Pricing>
				{renderFeatureList()}
				<S.LinkWrapper href={billingUrl} onClick={handleUpgradeRedirect}>
					<S.CTAButton variant="max">Upgrade to Max</S.CTAButton>
				</S.LinkWrapper>
			</S.Base>
		);
	}

	// variant === 'upgrade'
	const price = isYearly
		? tier === Tier.TIER_1
			? PRO_YEARLY
			: MAX_YEARLY
		: tier === Tier.TIER_1
		? PRO_MONTHLY
		: MAX_MONTHLY;
	const upgradeUrl = `${VITE_FORGE_URL}/stripe/session?tier=${tier}&interval=${
		isYearly ? 'year' : 'month'
	}`;

	return (
		<S.Base tier={tier}>
			<S.Name tier={tier}>{tierName}</S.Name>
			<S.Pricing>
				<S.Price>
					${price}
					<S.PricePeriod>/mo</S.PricePeriod>
				</S.Price>
				{isYearly && <S.SavingsBadge tier={tier}>Save 15%</S.SavingsBadge>}
			</S.Pricing>
			{renderFeatureList()}
			<S.LinkWrapper href={upgradeUrl} onClick={handleUpgradeRedirect}>
				<S.CTAButton variant={tier === Tier.TIER_1 ? 'pro' : 'max'}>
					Upgrade to {tierName}
				</S.CTAButton>
			</S.LinkWrapper>
		</S.Base>
	);
};

export default TierCard;
